import React, { useContext } from 'react';
import Lazy from '@prisjakt/ui-lazy';
import PriceRow from '../price-row/row-types/regular';
import PriceRowOld from '../price-row/row-types/regular-old';
import { getValidPriceType } from '../utils';
import { Brand, Maybe, Price, ProductMedia } from '../../../../../types';
import { PriceDisplay, CurrentCampaign } from 'src/areas/product/types';
import { PriceListFilterContext } from '../../../price-list-filter-context';
import { RowCard, Row } from '../price-row/row-types/styles';
import { useDispatch } from 'react-redux';
import { useDeliveryTimeExperiment } from 'src/hooks/experiments';
import {
  productPriceClick,
  productClickOutBounce,
} from 'src/areas/product/actions/product';

const PriceListTableRow: React.FC<IPriceListTableRowProps> = props => {
  const { item, index, campaign, ...priceRowProps } = props;
  const { filters } = useContext(PriceListFilterContext);
  const rowKey = `${item.id}-${index}`;
  const deliveryTimeExperimentVal = useDeliveryTimeExperiment();
  const dispatch = useDispatch();

  if (deliveryTimeExperimentVal) {
    return wrapLazy(
      index,
      rowKey,
      <PriceRow
        key={rowKey}
        name={item.name}
        {...priceRowProps}
        onClickOutBounce={() => {
          dispatch(productClickOutBounce(priceRowProps.productId, item.id));
        }}
        priceId={item.id}
        priceType={getValidPriceType(item.type)}
        price={item.price}
        deliveryOptions={item.deliveryOptions}
        store={item.store}
        storeInfo={
          item.store && item.store.providedByStore
            ? item.store.providedByStore.generalInformation
            : null
        }
        stock={item.stock}
        externalUri={item.externalUri}
        alternativePrices={item.alternativePrices}
        openInNewWindow
        brandRecommendedRetailer={Boolean(item.brandRecommendedRetailer)}
        isInternationalPrice={Boolean(
          filters.showInternational &&
            item.store &&
            item.store.market === 'secondary'
        )}
        isMemberPrice={Boolean(item.isMemberPrice)}
        shipping={item.shipping}
        deliveryTimeExperimentVal={deliveryTimeExperimentVal}
        campaign={getCampaignById(item, campaign)}
        onPriceClick={(
          featured: boolean,
          price: number | undefined,
          status: string,
          isAlternative: boolean
        ) => {
          // NOTE: Any changes done to data or structure of PRODUCT_PRICE_CLICK needs to be
          // reflected in react/middlewares/out pre click for tracking clicks before JS bundle
          dispatch(
            productPriceClick(
              priceRowProps.productId,
              price || item,
              featured,
              status,
              isAlternative,
              false
            )
          );
        }}
      />
    );
  }

  // Show old price row. Can be removed after experiment
  return wrapLazy(
    index,
    rowKey,
    <PriceRowOld
      key={rowKey}
      name={item.name}
      {...priceRowProps}
      onClickOutBounce={() => {
        dispatch(productClickOutBounce(priceRowProps.productId, item.id));
      }}
      priceId={item.id}
      priceType={getValidPriceType(item.type)}
      price={item.price}
      deliveryOptions={item.deliveryOptions}
      store={item.store}
      storeInfo={
        item.store && item.store.providedByStore
          ? item.store.providedByStore.generalInformation
          : null
      }
      stock={item.stock}
      externalUri={item.externalUri}
      alternativePrices={item.alternativePrices}
      openInNewWindow
      brandRecommendedRetailer={Boolean(item.brandRecommendedRetailer)}
      isInternationalPrice={Boolean(
        filters.showInternational &&
          item.store &&
          item.store.market === 'secondary'
      )}
      isMemberPrice={Boolean(item.isMemberPrice)}
      shipping={item.shipping}
      campaign={getCampaignById(item, campaign)}
      onPriceClick={(
        featured: boolean,
        price: number | undefined,
        status: string,
        isAlternative: boolean
      ) => {
        // NOTE: Any changes done to data or structure of PRODUCT_PRICE_CLICK needs to be
        // reflected in react/middlewares/out pre click for tracking clicks before JS bundle
        dispatch(
          productPriceClick(
            priceRowProps.productId,
            price || item,
            featured,
            status,
            isAlternative,
            false
          )
        );
      }}
    />
  );
};

interface IPriceListTableRowProps {
  item: Price;
  index: number;
  productId: number;
  productName?: string;
  media: ProductMedia & { first?: string | null };
  brand?: Maybe<Brand>;
  campaign?: CurrentCampaign | null;
  priceDisplay: PriceDisplay;
  deliveryTime?: boolean;
}

const lazyPlaceholder = (
  <RowCard>
    <Row />
  </RowCard>
);

const getCampaignById = (item: Price, campaign: any) => {
  return campaign?.price &&
    campaign?.media &&
    campaign?.name &&
    item.id === campaign.price.id
    ? campaign
    : null;
};

const wrapLazy = (
  order: number,
  rowKey: string,
  component: React.ReactElement
) => {
  if (order > 10) {
    return (
      <Lazy placeholder={lazyPlaceholder} offset={200} key={rowKey}>
        {component}
      </Lazy>
    );
  }
  return component;
};

export default PriceListTableRow;
